import { Component, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MicaChatApiService } from '@core/services/api/mica-chat-api.service';
import { MicaChatService } from '@shared/services/mica-chat.service';
import { SucursalService } from '@shared/services/sucursal.service';
import { AppService } from '@shared/services/app.service';
import { ToastService } from '@shared/services/toast.service';
import {
  IFlujoWhatsapp,
  ICrearFlujoReq,
} from '@core/interfaces/request-response/flujo.dto.interface';
import { MicaChatFlujoEditor } from './mica-chat-flujo-editor';

@Component({
  selector: 'app-mica-chat-flujos',
  imports: [FormsModule, MicaChatFlujoEditor],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './mica-chat-flujos.html',
})
export class MicaChatFlujos {
  private readonly api = inject(MicaChatApiService);
  private readonly micaChatService = inject(MicaChatService);
  private readonly sucursalService = inject(SucursalService);
  private readonly appService = inject(AppService);
  private readonly toast = inject(ToastService);

  flujos = signal<IFlujoWhatsapp[]>([]);
  cargando = signal(false);
  flujoEditando = signal<IFlujoWhatsapp | null>(null);
  mostrarCrear = signal(false);
  nuevoNombre = signal('');
  nuevaDescripcion = signal('');

  get telefonoId(): number {
    return this.micaChatService.telefonoSeleccionadoSignal()?.id ?? 0;
  }

  constructor() {
    effect(() => {
      if (
        this.sucursalService.sucursalReady() &&
        this.appService.authReady() &&
        this.telefonoId > 0
      ) {
        this.cargarFlujos();
      }
    });
  }

  cargarFlujos() {
    if (!this.telefonoId) return;
    this.cargando.set(true);
    this.api.listarFlujos(this.telefonoId).subscribe({
      next: (data) => {
        this.flujos.set(data);
        this.cargando.set(false);
      },
      error: () => {
        this.cargando.set(false);
      },
    });
  }

  abrirEditor(flujo: IFlujoWhatsapp) {
    this.cargando.set(true);
    this.api.obtenerFlujo(flujo.id).subscribe({
      next: (flujoCompleto) => {
        this.flujoEditando.set(flujoCompleto);
        this.cargando.set(false);
      },
      error: () => {
        this.toast.show('top-right', 'error', 'Error', 'No se pudo cargar el flujo');
        this.cargando.set(false);
      },
    });
  }

  cerrarEditor() {
    this.flujoEditando.set(null);
    this.cargarFlujos();
  }

  crearFlujo() {
    if (!this.nuevoNombre().trim()) return;
    const dto: ICrearFlujoReq = {
      nombre: this.nuevoNombre().trim(),
      descripcion: this.nuevaDescripcion().trim() || undefined,
    };
    this.api.crearFlujo(this.telefonoId, dto).subscribe({
      next: () => {
        this.toast.show('top-right', 'success', 'Creado', 'Flujo creado correctamente');
        this.nuevoNombre.set('');
        this.nuevaDescripcion.set('');
        this.mostrarCrear.set(false);
        this.cargarFlujos();
      },
      error: (err: any) =>
        this.toast.show('top-right', 'error', 'Error', err.error?.message || 'Error al crear'),
    });
  }

  toggleActivo(flujo: IFlujoWhatsapp) {
    this.api.toggleFlujo(flujo.id).subscribe({
      next: () => this.cargarFlujos(),
      error: (err: any) =>
        this.toast.show('top-right', 'error', 'Error', err.error?.message || 'Error'),
    });
  }

  eliminarFlujo(flujo: IFlujoWhatsapp) {
    this.api.eliminarFlujo(flujo.id).subscribe({
      next: () => {
        this.toast.show('top-right', 'success', 'Eliminado', `Flujo "${flujo.nombre}" eliminado`);
        this.cargarFlujos();
      },
      error: (err: any) =>
        this.toast.show('top-right', 'error', 'Error', err.error?.message || 'Error al eliminar'),
    });
  }
}
